import { useId, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Source } from "@re/shared";

interface Props {
  n: number;
  source?: Source;
  onClick?: (n: number) => void;
}

export function CitationMarker({ n, source, onClick }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span className="relative inline-block align-baseline">
      <button
        ref={btnRef}
        type="button"
        className="re-citation"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          onClick?.(n);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        [{n}]
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className="absolute left-0 top-full z-30 mt-1 w-72 rounded border bg-surface p-2 text-left text-xs shadow-lg"
          style={{ borderColor: "color-mix(in oklab, var(--re-text) 14%, transparent)" }}
        >
          {source ? (
            <>
              <span className="font-mono text-ink/40">#{n} · {source.adapter}</span>
              <span className="block font-medium text-ink">{source.title}</span>
              {source.url && (
                <a href={source.url} target="_blank" rel="noreferrer" className="block truncate text-a2 hover:underline">
                  {source.url}
                </a>
              )}
              {source.fetched_at && <span className="block text-ink/40">{source.fetched_at}</span>}
            </>
          ) : (
            <span className="text-ink/50">{t("citation.missing", { n })}</span>
          )}
        </span>
      )}
    </span>
  );
}
